import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Mail, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const { token, user } = response.data;

      if (user.role !== "admin") {
        toast.error("Access denied. Admin only.");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      toast.success(`Welcome, ${user.name}!`);
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 login-bg dark"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1760239037245-a372db8630f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHwzZCUyMGFic3RyYWN0JTIwZ2VvbWV0cmljJTIwc2hhcGVzJTIwYmx1ZSUyMHdoaXRlfGVufDB8fHx8MTc2NDM4MDg3N3ww&ixlib=rb-4.1.0&q=85')",
      }}
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-900/95"></div>
      
      <div className="relative w-full max-w-md glass-card rounded-2xl shadow-2xl p-8 fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-slate-300">
            <a href="http://nix.global/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              NIX Southeast Asia
            </a>
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-200 font-medium">
              Admin Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="admin@nix-southeast-asia.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 h-12 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                data-testid="admin-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-200 font-medium">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 h-12 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-lg shadow-blue-600/30 transition-all active:scale-95"
            data-testid="admin-login-submit-button"
          >
            {loading ? "Loading..." : "Login Admin"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login/employee"
            className="text-sm text-blue-400 hover:text-blue-300 font-medium"
            data-testid="employee-login-link"
          >
            ← Back to Employee Portal
          </Link>
        </div>
      </div>
    </div>
  );
}